<div class="target-selector-dialog">
    <!-- Header -->
    <div class="dialog-header">
        <div class="header-content">
            <mat-icon class="header-icon">group_add</mat-icon>
            <div class="header-text">
                <h2>Select Policy Targets</h2>
                <p class="subtitle">Choose who should receive this policy</p>
            </div>
        </div>
        <button mat-icon-button (click)="cancel()" class="close-btn">
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <!-- Content -->
    <div class="dialog-content">
        <!-- Target Mode Selection -->
        <div class="mode-selection">
            <mat-radio-group [(ngModel)]="targetMode" class="mode-group">
                <mat-radio-button value="all_organization" (change)="selectAllOrganization()">
                    <div class="mode-option">
                        <mat-icon>business</mat-icon>
                        <span>Entire Organization</span>
                    </div>
                </mat-radio-button>
                <mat-radio-button value="selected" (change)="selectTargetedMode()">
                    <div class="mode-option">
                        <mat-icon>checklist</mat-icon>
                        <span>Selected Targets</span>
                    </div>
                </mat-radio-button>
            </mat-radio-group>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions" *ngIf="targetMode === 'selected'">
            <span class="label">Quick Select:</span>
            <button mat-stroked-button (click)="selectAllOfType('division')" class="quick-btn">
                <mat-icon>account_tree</mat-icon>
                All Divisions
            </button>
            <button mat-stroked-button (click)="selectAllOfType('department')" class="quick-btn">
                <mat-icon>folder</mat-icon>
                All Departments
            </button>
            <button mat-stroked-button (click)="selectAllOfType('unit')" class="quick-btn">
                <mat-icon>workspaces</mat-icon>
                All Units
            </button>
            <button mat-stroked-button (click)="clearAllSelections()" class="quick-btn clear-btn">
                <mat-icon>clear_all</mat-icon>
                Clear All
            </button>
        </div>

        <!-- Search -->
        <div class="search-bar" *ngIf="targetMode === 'selected'">
            <mat-form-field appearance="outline" class="search-field">
                <mat-icon matPrefix>search</mat-icon>
                <input matInput 
                       [(ngModel)]="searchTerm" 
                       (ngModelChange)="applySearch()"
                       placeholder="Search organization structure...">
                <button mat-icon-button matSuffix *ngIf="searchTerm" (click)="clearSearch()">
                    <mat-icon>close</mat-icon>
                </button>
            </mat-form-field>
        </div>

        <!-- Organization Tree -->
        <div class="tree-container" *ngIf="targetMode === 'selected'">
            <mat-tree [dataSource]="dataSource" [treeControl]="treeControl" class="org-tree">
                <!-- Leaf nodes (no children) -->
                <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding [matTreeNodePaddingIndent]="24">
                    <button mat-icon-button disabled class="toggle-spacer"></button>
                    
                    <mat-checkbox 
                        [checked]="node.isSelected || node.isIncludedViaParent"
                        [indeterminate]="node.isIncludedViaParent"
                        [disabled]="node.isIncludedViaParent || data.mode === 'view'"
                        (change)="toggleNodeSelection(node)"
                        class="node-checkbox">
                    </mat-checkbox>

                    <mat-icon class="part-icon" [class]="'part-type-' + node.partType">
                        {{ getPartIcon(node.partType) }}
                    </mat-icon>

                    <span class="node-name" [class.included-via-parent]="node.isIncludedViaParent">
                        {{ node.name }}
                    </span>

                    <span class="node-stats">
                        <span class="stat" *ngIf="node.hatCount > 0">
                            <mat-icon>badge</mat-icon>
                            {{ node.hatCount }}
                        </span>
                        <span class="stat" *ngIf="node.userCount > 0">
                            <mat-icon>person</mat-icon>
                            {{ node.userCount }}
                        </span>
                    </span>

                    <!-- Selection indicator for leaf nodes -->
                    <mat-icon class="selected-indicator" *ngIf="node.isSelected && data.mode !== 'view'">check_circle</mat-icon>
                </mat-tree-node>

                <!-- Nodes with children (nested structure) -->
                <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChildren">
                    <div class="mat-tree-node">
                        <button mat-icon-button matTreeNodeToggle class="toggle-btn">
                            <mat-icon>
                                {{ treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                            </mat-icon>
                        </button>

                        <mat-checkbox 
                            [checked]="node.isSelected || node.isIncludedViaParent"
                            [indeterminate]="node.isIncludedViaParent"
                            [disabled]="node.isIncludedViaParent || data.mode === 'view'"
                            (change)="toggleNodeSelection(node)"
                            class="node-checkbox">
                        </mat-checkbox>

                        <mat-icon class="part-icon" [class]="'part-type-' + node.partType">
                            {{ getPartIcon(node.partType) }}
                        </mat-icon>

                        <span class="node-name" 
                              [class.selected]="node.isSelected"
                              [class.included-via-parent]="node.isIncludedViaParent">
                            {{ node.name }}
                        </span>

                        <span class="node-stats">
                            <span class="stat" *ngIf="node.hatCount > 0">
                                <mat-icon>badge</mat-icon>
                                {{ node.hatCount }}
                            </span>
                            <span class="stat" *ngIf="node.userCount > 0">
                                <mat-icon>person</mat-icon>
                                {{ node.userCount }}
                            </span>
                        </span>

                        <!-- Selection Actions -->
                        <div class="node-actions" *ngIf="node.isSelected && data.mode !== 'view'">
                            <!-- Select All Children Button -->
                            <button mat-stroked-button 
                                    class="select-children-btn"
                                    (click)="selectAllChildrenRecursive(node, $event)"
                                    [disabled]="node.includeChildren || areAllChildrenSelected(node)"
                                    matTooltip="Select all children as separate targets">
                                <mat-icon>account_tree</mat-icon>
                                <span class="btn-text">Select Children</span>
                            </button>

                            <!-- Cascade Toggle (include as bundle) -->
                            <button mat-icon-button 
                                    [class.active]="node.includeChildren"
                                    (click)="toggleIncludeChildren(node, $event)"
                                    [matTooltip]="node.includeChildren ? 'Click to unbundle children' : 'Auto-include all current & future children'">
                                <mat-icon>{{ node.includeChildren ? 'link' : 'link_off' }}</mat-icon>
                            </button>
                        </div>

                        <!-- Status indicator when children are bundled -->
                        <span class="cascade-indicator" *ngIf="node.includeChildren">
                            <mat-icon>subdirectory_arrow_right</mat-icon>
                            <span>+{{ countAllDescendants(node) }} children</span>
                        </span>
                    </div>
                    
                    <!-- This is where children are rendered! -->
                    <div class="nested-children" [class.hidden]="!treeControl.isExpanded(node)">
                        <ng-container matTreeNodeOutlet></ng-container>
                    </div>
                </mat-nested-tree-node>
            </mat-tree>

            <!-- Empty State -->
            <div class="empty-state" *ngIf="orgTreeData.length === 0">
                <mat-icon>account_tree</mat-icon>
                <p>No organization structure available</p>
            </div>
        </div>

        <!-- Entire Organization Message -->
        <div class="all-org-message" *ngIf="targetMode === 'all_organization'">
            <mat-icon class="large-icon">business</mat-icon>
            <h3>Entire Organization Selected</h3>
            <p>This policy will apply to all current and future members of the organization.</p>
        </div>
    </div>

    <!-- Summary Footer -->
    <div class="dialog-summary">
        <div class="summary-stats">
            <div class="stat-item">
                <mat-icon>account_tree</mat-icon>
                <span class="stat-value">{{ summary.partCount }}</span>
                <span class="stat-label">{{ summary.partCount === 1 ? 'Part' : 'Parts' }}</span>
            </div>
            <div class="stat-item">
                <mat-icon>badge</mat-icon>
                <span class="stat-value">{{ summary.hatCount }}</span>
                <span class="stat-label">{{ summary.hatCount === 1 ? 'Hat' : 'Hats' }}</span>
            </div>
            <div class="stat-item">
                <mat-icon>people</mat-icon>
                <span class="stat-value">{{ summary.userCount }}</span>
                <span class="stat-label">{{ summary.userCount === 1 ? 'Staff Member' : 'Staff' }}</span>
            </div>
        </div>
    </div>

    <!-- Actions -->
    <div class="dialog-actions">
        <button mat-button (click)="cancel()">Cancel</button>
        <button mat-flat-button color="primary" (click)="confirm()" [disabled]="data.mode === 'view'">
            <mat-icon>check</mat-icon>
            Confirm Selection
        </button>
    </div>
</div>
